import React, { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp } from "lucide-react";
import { api } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";

export default function PriceHistoryChart({ cardId, refreshKey }) {
  const [history, setHistory] = useState([]);
  const { rate, symbol, currency } = useCurrency() || { rate: 1, symbol: "$", currency: "USD" };
  const decimals = currency === "JPY" ? 0 : 2;

  useEffect(() => {
    api.get(`/cards/${cardId}/price-history`)
      .then((res) => setHistory(res.data.history || []))
      .catch(() => {});
  }, [cardId, refreshKey]);

  if (!history.length) return null;

  const data = history.map((h) => ({
    date: new Date(h.recorded_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    price: +(h.price * rate).toFixed(decimals),
  }));

  return (
    <div data-testid="price-history-panel" className="bg-[#111113] border border-white/10 rounded-xl p-5">
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1.5">
        <TrendingUp className="w-3.5 h-3.5 text-[#A78BFA]" /> Price history
      </p>
      {data.length < 2 ? (
        <p data-testid="price-history-single-point" className="text-xs text-zinc-500 mt-2">
          First snapshot recorded: <span className="font-mono-plex text-[#A78BFA]">{symbol}{data[0].price.toFixed(decimals)}</span> ({data[0].date}).
          The trend chart builds automatically every time this card is re-priced.
        </p>
      ) : (
        <div className="h-40 mt-2">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <AreaChart data={data} margin={{ top: 5, right: 5, bottom: 0, left: -18 }}>
              <defs>
                <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#A78BFA" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#A78BFA" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${symbol}${v}`} domain={["auto", "auto"]} />
              <Tooltip
                contentStyle={{ background: "#18181b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "#a1a1aa" }}
                formatter={(value) => [`${symbol}${Number(value).toFixed(decimals)}`, "NM price"]}
              />
              <Area type="monotone" dataKey="price" stroke="#A78BFA" strokeWidth={2} fill="url(#priceGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
